import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import AlertComponent from "../../components/AlertComponent";
import Loading from "../../components/Loading";
import { Context } from "../../Reducers/Store";
import { formatarDataHora, formatarDuracao, statusConfig } from "../../utils/deployStatus";
import { canInteractWithDeploy } from "../../utils/deployPermissions";

const STEP_LABELS = {
  ValidatingBranch: "Validando branch",
  CheckingOutBranch: "Checkout da branch",
  RestoringDatabase: "Restaurando banco",
  BuildingApplication: "Build da aplicação",
  PublishingArtifacts: "Publicando artefatos",
  DeployingToTarget: "Publicando no ambiente",
  RunningMigrations: "Executando migrations",
  WarmingUp: "Aquecendo aplicação",
  Completed: "Concluído",
};

function prettyStepName(stepName) {
  if (!stepName) return "—";
  return STEP_LABELS[stepName] || stepName.replace(/([A-Z])/g, " $1").trim();
}

function calcStepDuration(step) {
  if (!step.startedAt || !step.finishedAt) return null;
  const diffMs = new Date(step.finishedAt) - new Date(step.startedAt);
  if (Number.isNaN(diffMs) || diffMs < 0) return null;
  const totalSec = Math.floor(diffMs / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function DeployDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state] = useContext(Context);
  const [deploy, setDeploy] = useState(null);
  const [recipientsExpanded, setRecipientsExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${process.env.REACT_APP_API}/deployment-history/${id}`)
      .then((res) => { if (!cancelled) setDeploy(res.data); })
      .catch((err) => {
        console.error(err);
        AlertComponent("Erro", "Erro ao carregar detalhes do deploy", { icon: "error" });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  const isRunning = deploy?.status === "Running" || deploy?.status === "Pending";
  const isCancellableStatus = isRunning || deploy?.status === "Queued";
  const canCancel = isCancellableStatus && canInteractWithDeploy(deploy, state.profile);
  const canRetry = deploy?.status === "Failed" && canInteractWithDeploy(deploy, state.profile);

  const handleCancel = async () => {
    const confirmado = await AlertComponent(
      "Cancelar deploy",
      "Tem certeza que deseja cancelar este deploy? Essa ação não pode ser desfeita.",
      {
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sim, cancelar",
        cancelButtonText: "Voltar",
        confirmButtonColor: "#dc2626",
      },
      null,
      false
    );
    if (!confirmado?.isConfirmed) return;

    setCancelling(true);
    try {
      await axios.post(`${process.env.REACT_APP_API}/deployments/${id}/cancel`);
      const res = await axios.get(`${process.env.REACT_APP_API}/deployment-history/${id}`, {
        metadata: { ignoreLoading: true },
      });
      setDeploy(res.data);
      AlertComponent("Cancelado", "Deploy cancelado com sucesso.", { icon: "success" });
    } catch (error) {
      const mensagem = error?.response?.data?.message || error?.message || "Erro ao cancelar deploy";
      AlertComponent("Erro", mensagem, { icon: "error" });
    } finally {
      setCancelling(false);
    }
  };

  const handleRetry = async () => {
    if (!canRetry || retrying) return;
    const confirmado = await AlertComponent(
      "Tentar novamente",
      "Um novo deploy será iniciado com o mesmo ambiente, branch e opções da execução que falhou.",
      {
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Tentar novamente",
        cancelButtonText: "Voltar",
        confirmButtonColor: "#7c3aed",
      },
      null,
      false
    );
    if (!confirmado?.isConfirmed) return;

    setRetrying(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API}/deployments/${id}/retry`);
      const newDeployId = res.data?.id;
      AlertComponent("Iniciado", "Nova tentativa de deploy iniciada com sucesso.", { icon: "success" });
      if (newDeployId) {
        navigate(`/deploys/${newDeployId}`);
      }
    } catch (error) {
      const mensagem = error?.response?.data?.message || error?.message || "Erro ao tentar novamente";
      AlertComponent("Erro", mensagem, { icon: "error" });
    } finally {
      setRetrying(false);
    }
  };

  useEffect(() => {
    if (!isRunning) return;
    const interval = setInterval(() => {
      axios
        .get(`${process.env.REACT_APP_API}/deployment-history/${id}`, {
          metadata: { ignoreLoading: true },
        })
        .then((res) => setDeploy(res.data))
        .catch(() => {});
    }, 5000);
    return () => clearInterval(interval);
  }, [isRunning, id]);

  if (loading) return <Loading />;
  if (!deploy) {
    return (
      <div className="max-w-3xl mx-auto bg-white rounded-2xl p-10 text-center ring-1 ring-slate-200/60">
        <p className="text-slate-600 font-semibold mb-2">Deploy não encontrado</p>
        <button
          onClick={() => navigate("/deploys")}
          className="text-sm text-violet-600 hover:text-violet-800 font-medium"
        >
          ← Voltar para listagem
        </button>
      </div>
    );
  }

  const st = statusConfig(deploy.lastStepStatus || deploy.status);
  const target = deploy.target;
  const ambiente = target?.displayName || target?.siteName || "—";
  const autor = deploy.requestedBy?.displayName || deploy.requestedBy?.userName || "—";
  const steps = [...(deploy.steps || [])].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header card */}
      <div
        className="rounded-2xl p-6 sm:p-8 mb-6 relative overflow-hidden text-white"
        style={{ background: "linear-gradient(135deg, #1e1b4b 0%, #3b0764 100%)" }}
      >
        <div className="absolute -top-10 -right-10 w-52 h-52 rounded-full opacity-10" style={{ background: "radial-gradient(circle,#a78bfa,transparent)" }} />
        <div className="absolute -bottom-12 -left-6 w-40 h-40 rounded-full opacity-10" style={{ background: "radial-gradient(circle,#c4b5fd,transparent)" }} />

        <div className="relative flex items-center gap-4">
          <button
            onClick={() => navigate("/deploys")}
            className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition backdrop-blur-sm shrink-0"
            aria-label="Voltar"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full ${st.badgeClass}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${st.dotClass}`} />
                  {st.label}
                </span>
                {target?.isProduction && (
                  <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-rose-500/20 text-rose-200 ring-1 ring-rose-400/30">
                    Produção
                  </span>
                )}
                {deploy.restoreDatabaseRequested && (
                  <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-violet-500/20 text-violet-200 ring-1 ring-violet-400/30">
                    BD restaurado
                  </span>
                )}
              </div>
              <h1 className="text-xl sm:text-2xl font-bold font-mono truncate">{deploy.branchName}</h1>
              <p className="text-purple-200 text-sm mt-1">
                Deploy para <span className="font-semibold text-white">{ambiente}</span> por <span className="font-semibold text-white">{autor}</span>
              </p>
            </div>
            <div className="shrink-0 flex flex-col items-start sm:items-end gap-3 sm:pl-4">
              <div className="sm:text-right">
                <p className="text-xs uppercase tracking-wider text-purple-300 font-semibold">Duração total</p>
                <p className="text-2xl font-extrabold tabular-nums">{formatarDuracao(deploy.duration)}</p>
              </div>
              {canRetry && (
                <button
                  onClick={handleRetry}
                  disabled={retrying}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-violet-950 bg-white hover:bg-violet-50 shadow-sm shadow-purple-950/20 hover:shadow-purple-950/40 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {retrying ? (
                    <>
                      <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                      Tentando...
                    </>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                        <path d="M21 3v6h-6" />
                      </svg>
                      Tentar novamente
                    </>
                  )}
                </button>
              )}
              {canCancel && (
                <button
                  onClick={handleCancel}
                  disabled={cancelling || retrying}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-white bg-red-600 hover:bg-red-700 shadow-sm shadow-red-900/20 hover:shadow-red-900/40 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {cancelling ? (
                    <>
                      <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                      Cancelando...
                    </>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728A9 9 0 015.636 5.636" />
                      </svg>
                      Cancelar deploy
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Metadados */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <InfoCard label="Criado em" value={formatarDataHora(deploy.createdAt)} />
        {deploy.dataAgendamento && (
          <InfoCard label="Agendado para" value={formatarDataHora(deploy.dataAgendamento)} />
        )}
        {deploy.dataInicioStartAgendamento && (
          <InfoCard label="Disparado pelo agendador em" value={formatarDataHora(deploy.dataInicioStartAgendamento)} />
        )}
        <InfoCard label="Iniciado em" value={formatarDataHora(deploy.startedAt)} />
        <InfoCard label="Finalizado em" value={formatarDataHora(deploy.finishedAt)} />
        <InfoCard label="Etapa atual" value={prettyStepName(deploy.currentStep)} />
      </div>

      {/* Observação */}
      {deploy.observation && (
        <div className="mb-6 rounded-2xl bg-white ring-1 ring-slate-200/60 p-5">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center shrink-0">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M21 15a4 4 0 0 1-4 4H7l-4 4V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold uppercase tracking-wider text-violet-700 mb-1">Obs. do deploy</p>
              <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">{deploy.observation}</p>
            </div>
          </div>
        </div>
      )}

      {/* Erro (se houver) */}
      {deploy.errorMessage && (
        <div className="mb-6 rounded-2xl bg-red-50 ring-1 ring-red-200 p-5">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-red-100 text-red-600 flex items-center justify-center shrink-0">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M4.93 19h14.14a2 2 0 001.74-3l-7.07-12a2 2 0 00-3.48 0l-7.07 12a2 2 0 001.74 3z" />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold uppercase tracking-wider text-red-700 mb-1">Erro na execução</p>
              <pre className="text-sm text-red-900 whitespace-pre-wrap break-words font-mono">{deploy.errorMessage}</pre>
            </div>
          </div>
        </div>
      )}

      {/* Destinatários de notificação */}
      {Array.isArray(deploy.notificationRecipients) && deploy.notificationRecipients.length > 0 && (
        <div className="mb-6 bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/60 overflow-hidden">
          <button
            type="button"
            onClick={() => setRecipientsExpanded((v) => !v)}
            className="w-full flex items-center justify-between gap-3 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 transition text-left"
          >
            <div className="flex items-center gap-2 min-w-0">
              <svg className="w-4 h-4 text-violet-600 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <h2 className="text-base font-bold text-slate-800">Pessoas notificadas</h2>
              <span className="ml-1 px-2 py-0.5 text-xs font-semibold bg-violet-100 text-violet-700 rounded-full">
                {deploy.notificationRecipients.length}
              </span>
            </div>
            <svg
              className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${recipientsExpanded ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {recipientsExpanded && (
            <ul className="divide-y divide-slate-100">
              {deploy.notificationRecipients.map((r) => (
                <li key={r.userId} className="flex items-center gap-3 px-6 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-slate-800 truncate">
                        {r.displayName || r.email}
                      </p>
                      {r.isRequester && (
                        <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-amber-100 text-amber-700 ring-1 ring-amber-200">
                          Solicitante
                        </span>
                      )}
                      {r.notifyTelegramOnDeploymentCompletion && r.hasTelegramChat && (
                        <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-sky-100 text-sky-700 ring-1 ring-sky-200">
                          Telegram
                        </span>
                      )}
                      {r.notifyWhatsappOnDeploymentCompletion && r.hasWhatsappPhone && (
                        <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-lime-100 text-lime-700 ring-1 ring-lime-200">
                          WhatsApp
                        </span>
                      )}
                      {r.notifyEmailOnDeploymentCompletion && (
                        <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
                          E-mail
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 truncate">{r.email}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Timeline de steps */}
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/60 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-violet-500" />
            <h2 className="text-base font-bold text-slate-800">Etapas do deploy</h2>
            <span className="ml-1 px-2 py-0.5 text-xs font-semibold bg-violet-100 text-violet-700 rounded-full">
              {steps.length}
            </span>
          </div>
        </div>

        {steps.length === 0 ? (
          <div className="text-center py-16 px-6">
            <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <p className="text-slate-500 font-medium">Nenhuma etapa registrada</p>
          </div>
        ) : (
          <ol className="relative">
            {steps.map((step, idx) => {
              const sst = statusConfig(step.status);
              const isLast = idx === steps.length - 1;
              const stepDur = calcStepDuration(step);
              return (
                <li key={step.id} className="relative">
                  <div className="flex gap-4 px-6 py-5">
                    {/* Coluna da timeline */}
                    <div className="flex flex-col items-center shrink-0">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center ring-4 ring-white ${sst.iconBg} ${sst.iconColor}`}>
                        {sst.icon}
                      </div>
                      {!isLast && <div className="flex-1 w-px bg-slate-200 mt-1 mb-[-20px] min-h-[20px]" />}
                    </div>

                    {/* Conteúdo */}
                    <div className="flex-1 min-w-0 pb-3">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-mono text-slate-400 tabular-nums">#{step.sequence}</span>
                            <p className="font-semibold text-slate-800">{prettyStepName(step.stepName)}</p>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full ${sst.badgeClass}`}>
                              <span className={`w-1 h-1 rounded-full ${sst.dotClass}`} />
                              {sst.label}
                            </span>
                          </div>
                          {step.message && (
                            <p className="text-sm text-slate-600 mt-1.5 whitespace-pre-wrap break-all">
                              {step.message}
                            </p>
                          )}
                        </div>
                        <div className="text-right shrink-0 ml-auto text-xs text-slate-400 tabular-nums">
                          {stepDur && <p className="font-semibold text-slate-600">{formatarDuracao(stepDur)}</p>}
                          <p>{formatarDataHora(step.startedAt)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {!isLast && <div className="border-t border-slate-50" />}
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {/* ID no final */}
      <p className="text-center text-[11px] text-slate-400 mt-6">
        ID do deploy: <span className="font-mono">{deploy.id}</span>
      </p>
    </div>
  );
}

function InfoCard({ label, value }) {
  return (
    <div className="bg-white rounded-2xl p-4 ring-1 ring-slate-200/60">
      <p className="text-[11px] font-bold uppercase tracking-wider text-violet-600 mb-1">{label}</p>
      <p className="text-sm font-semibold text-slate-800">{value}</p>
    </div>
  );
}
